import React, { useState } from 'react';
import { Column, HeaderGroup, useSortBy, useTable } from 'react-table';
import { Button, Card, CardBody, CustomInput, InputGroup, InputGroupAddon, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { parseCSVToSSOUsers, ParsedSSOUser, SSO_CSV_TEMPLATE_CONTENT } from '.';
import { createSSOUsersForAccount } from '../../api/users';
import { getRoles } from '../../api/roles';
import { toast } from 'react-toastify';
import { useUserContext } from '../../contexts/UserContext';
import { useHistory } from 'react-router';
import AccessControl from '../userprofile/accesscontrol';
import styles from '../../styles/archiveui.module.css';

const SSOUserImport = (): JSX.Element => {
  const {
    profile: { permissions },
    currentAccount,
  } = useUserContext();
  const history = useHistory();

  let userAccessForPage: any;
  if (permissions[currentAccount.accountId] != undefined) {
    userAccessForPage = permissions[currentAccount.accountId]['users']?.actions.create;
  }

  React.useEffect(() => {
    if (userAccessForPage === 'DENY' || userAccessForPage === 'NONE' || permissions === undefined) {
      toast.error('Not Authorized');
      history.push('/notauthorized');
    }
  }, [currentAccount]);

  const [validRoleNames, setValidRoleNames] = useState<Set<string> | undefined>(undefined);

  React.useEffect(() => {
    getRoles(currentAccount.accountId)
      .then((roles) => setValidRoleNames(new Set(roles.map((r) => r.name))))
      .catch(() => setValidRoleNames(undefined));
  }, [currentAccount.accountId]);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [usersToImport, setUsersToImport] = useState<ParsedSSOUser[]>([]);
  const [selectedFile, setSelectedFile] = useState<File>();

  const toggleImportModal = () => setImportModalOpen((v) => !v);

  const clearUsers = () => {
    setUsersToImport([]);
    setSelectedFile(undefined);
  };

  const downloadTemplate = () => {
    const blob = new Blob([SSO_CSV_TEMPLATE_CONTENT], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sso-users-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const fileChange = (e: React.FormEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.item(0);
    if (file) setSelectedFile(file);
  };

  const startImport = async () => {
    if (!selectedFile) return;
    const text = await selectedFile.text();
    setUsersToImport(parseCSVToSSOUsers(text, validRoleNames));
  };

  const toggleSelected = (rowIndex: number) => {
    setUsersToImport((prev) => prev.map((u) => (u.rowIndex === rowIndex && u.valid ? { ...u, selectedForImport: !u.selectedForImport } : u)));
  };

  const validCount = usersToImport.filter((u) => u.valid).length;
  const selectedCount = usersToImport.filter((u) => u.selectedForImport).length;

  const doImport = async () => {
    toggleImportModal();
    const users = usersToImport
      .filter((u) => u.selectedForImport)
      .map(({ externalUserId, role, givenName, familyName, email }) => ({
        externalUserId,
        role,
        givenName,
        familyName,
        email,
      }));
    await toast.promise(
      createSSOUsersForAccount(currentAccount.accountId, users).then((result) => {
        return result.message;
      }),
      {
        pending: 'Importing SSO users...',
        success: { render: ({ data }) => data as string },
        error: 'There was an error importing SSO users',
      },
    );
    clearUsers();
  };

  const columns = React.useMemo(
    () =>
      [
        {
          Header: 'External User ID',
          accessor: 'externalUserId',
          Cell: ({ row, value }: any) => (
            <span style={{ color: row.original.valid ? 'inherit' : '#b91c1c', fontWeight: row.original.valid ? 'normal' : 'bold' }}>
              {value || <em>missing</em>}
            </span>
          ),
        },
        {
          Header: 'Role',
          accessor: 'role',
          Cell: ({ row, value }: any) => (
            <span style={{ color: row.original.valid ? 'inherit' : '#b91c1c', fontWeight: row.original.valid ? 'normal' : 'bold' }}>
              {value || <em>missing</em>}
            </span>
          ),
        },
        { Header: 'Given Name', accessor: 'givenName' },
        { Header: 'Family Name', accessor: 'familyName' },
        { Header: 'Email', accessor: 'email' },
        {
          Header: 'Status',
          accessor: 'validationError',
          Cell: ({ row, value }: any) => {
            if (value) return <span style={{ color: '#b91c1c' }}>{value}</span>;
            return (
              <span style={{ cursor: 'pointer' }} onClick={() => toggleSelected(row.original.rowIndex)}>
                {row.original.selectedForImport ? '✅' : '⛔️'}
              </span>
            );
          },
        },
      ] as Column<ParsedSSOUser>[],
    [],
  );

  const data = React.useMemo(() => usersToImport, [usersToImport]);
  const { headerGroups, rows, prepareRow } = useTable({ columns, data }, useSortBy);

  const renderSort = (column: HeaderGroup<ParsedSSOUser>) => {
    if (column.isSorted) return <span>{column.isSortedDesc ? ' 🔽' : ' 🔼'}</span>;
    return <span />;
  };

  return (
    <div>
      <Card style={{ marginTop: '1em', borderRadius: '.5em' }}>
        <CardBody className={styles.searchBarHeader}>
          <h5 style={{ textAlign: 'start', fontWeight: 500, marginTop: '.5em' }}>Import SSO Users</h5>
        </CardBody>
        <CardBody className={styles.searchBarBody}>
          <div className="row align-items-center">
            <div className="col">
              <InputGroup>
                <CustomInput id="ssoFileInput" type="file" onChange={fileChange} accept=".csv" />
                <InputGroupAddon addonType="append">
                  <Button className={styles.scrbBtnBlue} disabled={!selectedFile} onClick={startImport}>
                    Parse File
                  </Button>
                  <Button color="danger" onClick={clearUsers}>
                    Clear
                  </Button>
                </InputGroupAddon>
              </InputGroup>
            </div>
            <div className="col-md-auto">
              <Button color="secondary" onClick={downloadTemplate} title="Download a CSV template with the required column headers">
                Download Template
              </Button>
            </div>
            <div className="col-md-auto">
              <Button className={styles.scrbBtnOrange} onClick={toggleImportModal} disabled={selectedCount === 0}>
                Import Selected ({selectedCount})
              </Button>
            </div>
          </div>
          {usersToImport.length > 0 && (
            <div className="row mt-2">
              <div className="col-md-auto">
                <Button className={styles.scrbBtnMrgnR} onClick={() => setUsersToImport((prev) => prev.map((u) => ({ ...u, selectedForImport: u.valid })))}>
                  Select All Valid
                </Button>
                <Button className={styles.scrbBtnMrgnR} onClick={() => setUsersToImport((prev) => prev.map((u) => ({ ...u, selectedForImport: false })))}>
                  Deselect All
                </Button>
              </div>
              <div className="col d-flex align-items-center">
                <small style={{ color: '#555' }}>
                  {usersToImport.length} row(s) parsed &mdash; {validCount} valid, {usersToImport.length - validCount} invalid
                </small>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {usersToImport.length > 0 && (
        <Card style={{ marginTop: '1em', borderRadius: '.5em' }}>
          <Table striped hover size="sm" responsive>
            <thead className={styles.tableHeader}>
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()} key={hg.getHeaderGroupProps().key}>
                  {hg.headers.map((col) => (
                    <th {...col.getHeaderProps(col.getSortByToggleProps())} key={col.getHeaderProps().key}>
                      {col.render('Header')}
                      {renderSort(col)}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={row.original.rowIndex}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} key={cell.getCellProps().key}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      <Modal toggle={toggleImportModal} isOpen={importModalOpen}>
        <ModalHeader>Import SSO Users</ModalHeader>
        <ModalBody>
          <p>
            Import {selectedCount} SSO user(s) into account <strong>{currentAccount.shortName}</strong>?
          </p>
          <p className="text-muted" style={{ fontSize: '0.9em' }}>
            Users will be created if they do not already exist, and added to this account. No Cognito accounts or invitation emails will be sent.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button className={styles.scrbBtnBlue} onClick={toggleImportModal}>
            Cancel
          </Button>
          <AccessControl permissionId="users" action="create">
            <Button type="submit" className={styles.scrbBtnOrange} onClick={doImport}>
              Confirm Import
            </Button>
          </AccessControl>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SSOUserImport;
